import * as Dialog from '@radix-ui/react-dialog'
import { Link } from 'react-router-dom'
import type { CartLine } from '../api/types'
import { useCart, zeilenname } from '../cart'
import { useT } from '../i18n'
import { usePriceFormat } from '../shopConfig'
import { Rabattfeld } from './Rabattfeld'
import { Fehlerhinweis, Ladehinweis } from './States'

/**
 * Der Warenkorb als Schublade.
 *
 * Schublade statt eigener Seite, weil das Hineinlegen kein Ortswechsel ist:
 * Wer weiterstöbern will, schließt sie und steht wieder da, wo er war — ohne
 * Zurückknopf und ohne die Liste neu zu laden.
 *
 * Gerechnet wird hier nichts mehr. Zwischensumme, Versand, Freigrenze und
 * Hinderungsgründe kommen fertig vom Server; was hier stünde, wäre eine zweite
 * Wahrheit, und die abweichende fiele erst an der Kasse auf.
 *
 * # Warum Radix und nicht weiter von Hand
 *
 * Das Verhalten eines modalen Fensters ist mehr, als es aussieht: Fokus
 * hineinlegen und beim Schließen zurückgeben, Escape, die Seite dahinter
 * feststellen, den Fokus drinnen halten, `aria-modal`, und auf dem Telefon die
 * Bildlaufsperre, ohne dass die Seite nach oben springt. Das stand hier als
 * drei useEffect-Blöcke — jeder für sich richtig, zusammen eine unvollständige
 * Nachbildung dessen, was Radix Dialog fertig mitbringt.
 *
 * Radix liefert Verhalten, keine Gestaltung. Das ist der Grund, warum es nicht
 * dem widerspricht, was oben in styles.css steht: Die Farben und die Schrift
 * bestimmt weiterhin der Händler, und es kommt kein fremdes Aussehen mit.
 */
export function CartDrawer() {
  const { offen, schliessen } = useCart()

  return (
    <Dialog.Root open={offen} onOpenChange={(jetztOffen) => !jetztOffen && schliessen()}>
      <Dialog.Portal>
        {/* Die Fläche daneben schließt — das erledigt Radix über den Overlay. */}
        <Dialog.Overlay className="hinterlegung" />
        <Schublade />
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function Schublade() {
  const t = useT()
  const { ansicht, laedt, arbeitet, fehler, gekuerzt, schliessen, setzeMenge, entferne } = useCart()
  const preis = usePriceFormat()

  const leer = ansicht.lines.length === 0
  // Was so nicht bestellt werden kann, sperrt den Weg zur Kasse — genauso wie
  // die Hinderungsgründe des Ladens.
  const unbestellbar = ansicht.lines.some((zeile) => zeile.unavailable)
  const gesperrt = ansicht.blockers.length > 0 || unbestellbar

  return (
    <Dialog.Content className="schublade" aria-describedby={undefined}>
      <div className="schublade-kopf">
        <Dialog.Title className="schublade-titel">{t('korb.titel')}</Dialog.Title>
        <Dialog.Close asChild>
          <button
            className="knopf knopf-schlicht"
            type="button"
            aria-label={t('korb.schliessenAria')}
          >
            {t('korb.schliessen')}
          </button>
        </Dialog.Close>
      </div>

        <div className="schublade-inhalt">
          {laedt && <Ladehinweis text={t('korb.laedt')} />}

          {!laedt && fehler != null && <Fehlerhinweis fehler={fehler} />}

          {!laedt && leer && (
            <p className="hinweis">
              {t('korb.leer')} {t('korb.stoebern')}{' '}
              <Link to="/suche">{t('korb.sortiment')}</Link>.
            </p>
          )}

          {!leer && (
            <ul className="korbliste">
              {ansicht.lines.map((zeile) => (
                <Korbzeile
                  key={zeile.lineId || zeile.variantId}
                  zeile={zeile}
                  gesperrt={arbeitet}
                  gewollt={gekuerzt[zeile.variantId]}
                  onMenge={(menge) => setzeMenge(zeile.variantId, menge)}
                  onEntfernen={() => entferne(zeile.variantId)}
                />
              ))}
            </ul>
          )}
        </div>

        {!leer && (
          <div className="schublade-fuss">
            {/* Der Rabattcode steht vor den Summen: Er ändert sie, und wer ihn
                darunter fände, hätte die Zahlen schon gelesen. */}
            <Rabattfeld />

            <p className="summe">
              <span>{t('korb.zwischensumme')}</span>
              <strong>{preis(ansicht.subtotalGross)}</strong>
            </p>

            {/* Was der Rabatt bringt, in einer eigenen Zeile und nicht in die
                Zwischensumme eingerechnet: Dass 8,99 € gespart wurden, ist der
                Grund, aus dem jemand den Code eingetippt hat. */}
            {ansicht.discountGross > 0 && (
              <p className="summe summe-rabatt">
                <span>{ansicht.discountName || t('rabatt.gewaehrt')}</span>
                <strong>−{preis(ansicht.discountGross)}</strong>
              </p>
            )}

            {/* Die wirksamste Zeile im ganzen Bezahlvorgang — deshalb steht sie
                schon hier und nicht erst an der Kasse. */}
            {ansicht.freeShippingMissing > 0 && (
              <p className="hinweis" role="status">
                {t('korb.freigrenze', { betrag: preis(ansicht.freeShippingMissing) })}
              </p>
            )}

            <p className="preis-hinweis">{t('korb.versandhinweis')}</p>

            {/* Die Sätze des Servers unverändert: Er kennt den
                Mindestbestellwert und die Lieferländer dieses Ladens. */}
            {ansicht.blockers.map((satz) => (
              <p className="hinweis hinweis-fehler" role="alert" key={satz}>
                {satz}
              </p>
            ))}

            {unbestellbar && (
              <p className="hinweis hinweis-fehler" role="alert">
                {t('korb.unbestellbar')}
              </p>
            )}

            {!gesperrt && (
              <Link className="knopf knopf-breit" to="/kasse" onClick={schliessen}>
                {t('korb.zurKasse')}
              </Link>
            )}

            <Dialog.Close asChild>
              <button className="knopf knopf-schlicht knopf-breit" type="button">
                {t('korb.weiterEinkaufen')}
              </button>
            </Dialog.Close>
          </div>
        )}
    </Dialog.Content>
  )
}

function Korbzeile({
  zeile,
  gesperrt,
  gewollt,
  onMenge,
  onEntfernen,
}: {
  zeile: CartLine
  /** Während eine Änderung läuft: kein zweiter Klick. */
  gesperrt: boolean
  /** Was zuletzt gewollt war, falls der Server weniger daraus gemacht hat. */
  gewollt?: number
  onMenge: (menge: number) => void
  onEntfernen: () => void
}) {
  const t = useT()
  const preis = usePriceFormat()
  const name = zeilenname(zeile, t('korb.unbekannterArtikel'))

  return (
    <li className="korbzeile">
      {zeile.imageUrl ? (
        <img className="korbbild" src={zeile.imageUrl} alt="" loading="lazy" decoding="async" />
      ) : (
        <div className="korbbild kachel-leer" aria-hidden="true">
          {t('kachel.ohneBild')}
        </div>
      )}

      <div className="korbzeile-text">
        <p className="korbname">
          {zeile.handle ? <Link to={`/produkt/${zeile.handle}`}>{name}</Link> : name}
        </p>

        {!zeile.unavailable && (
          <p className="kachel-preis">
            {t('korb.jeStueck', { preis: preis(zeile.unitGross) })}
            {zeile.quantity > 1 && <> · {t('korb.gesamtZeile', { preis: preis(zeile.totalGross) })}</>}
          </p>
        )}

        {/* Die gekürzte Menge muss der Kunde sehen — sonst springt die Zahl im
            Feld wortlos zurück, und er hält die Seite für kaputt.

            Zwei Wege führen hierher, und beide enden in demselben Satz: Der
            Server hat eine gespeicherte Menge gekürzt (`adjusted`), oder er hat
            gerade eben weniger genommen, als gewünscht war (`gewollt`). */}
        {!zeile.unavailable && (zeile.adjusted || (gewollt != null && gewollt > zeile.quantity)) && (
          <p className="verfuegbarkeit" data-lieferbar="nein" role="status">
            {t('korb.gekuerzt', { anzahl: zeile.quantity })}
          </p>
        )}

        {zeile.unavailable ? (
          <>
            <p className="verfuegbarkeit" data-lieferbar="nein" role="alert">
              {t('korb.zeileWeg')}
            </p>
            <div className="mengenwahl">
              <button
                className="verweisknopf"
                type="button"
                onClick={onEntfernen}
                disabled={gesperrt}
              >
                {t('korb.entfernen')}
              </button>
            </div>
          </>
        ) : (
          <div className="mengenwahl">
            <button
              className="knopf knopf-schlicht"
              type="button"
              disabled={gesperrt}
              onClick={() => onMenge(zeile.quantity - 1)}
              aria-label={t('korb.mengeVerringern', { name })}
            >
              −
            </button>
            <label className="nur-fuer-screenreader" htmlFor={`menge-${zeile.variantId}`}>
              {t('korb.menge', { name })}
            </label>
            <input
              id={`menge-${zeile.variantId}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={zeile.available ?? 99}
              value={zeile.quantity}
              disabled={gesperrt}
              onChange={(ereignis) => onMenge(Number(ereignis.target.value))}
            />
            <button
              className="knopf knopf-schlicht"
              type="button"
              /* Über den Bestand hinaus gäbe es nichts zu holen: Der Server
                 kürzte die Menge sofort wieder, und der Knopf sähe kaputt aus. */
              disabled={gesperrt || (zeile.available != null && zeile.quantity >= zeile.available)}
              onClick={() => onMenge(zeile.quantity + 1)}
              aria-label={t('korb.mengeErhoehen', { name })}
            >
              +
            </button>
            <button
              className="verweisknopf"
              type="button"
              onClick={onEntfernen}
              disabled={gesperrt}
            >
              {t('korb.entfernen')}
            </button>
          </div>
        )}
      </div>
    </li>
  )
}
